import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../context/LanguageContext';
import MarketImage from '../components/MarketImage';

const Markets = () => {
  const { slug } = useParams();
  const { t, isRTL } = useLanguage();

  const market = slug ? t.markets.find((m) => m.slug === slug) : null;
  const relatedMarkets = useMemo(
    () => (slug ? t.markets.filter((m) => m.slug !== slug).slice(0, 4) : []),
    [t.markets, slug]
  );

  // If a specific market is selected
  if (slug) {
    if (!market) {
      return (
        <div className="min-h-screen">
          <Header />
          <main className="pt-32 pb-20">
            <div className="max-w-7xl mx-auto px-4 text-center">
              <h1 className="text-3xl font-bold text-gray-900">Market not found</h1>
              <Link to="/markets" className="text-[#9B4B8C] hover:underline mt-4 inline-block">
                {t.common.backToMarkets}
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      );
    }

    return (
      <div className="min-h-screen">
        <Header />
        <main dir={isRTL ? 'rtl' : 'ltr'}>
          <section className="pt-32 pb-20 bg-gradient-to-br from-[#e8d4e5] via-[#d9c5d6] to-[#c5a3c0]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Link
                to="/markets"
                className={`inline-flex items-center text-[#9B4B8C] hover:text-[#7d3c71] mb-6 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <ArrowLeft className={`w-5 h-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
                {t.common.backToMarkets}
              </Link>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <span className="px-4 py-1 bg-[#9B4B8C] text-white text-sm font-medium rounded-full">
                  {t.common.markets}
                </span>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-6">
                  {market.title}
                </h1>
              </motion.div>
            </div>
          </section>

          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                <motion.div
                  initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <img
                    src={market.image}
                    alt={market.title}
                    className="w-full h-[400px] object-cover rounded-2xl shadow-xl"
                    style={{ display: market.image2 ? 'none' : 'block' }}
                  />
                  {market.image2 && (
                    <div className="grid grid-cols-2 gap-1 w-full h-[400px] rounded-2xl shadow-xl overflow-hidden">
                      <img src={market.image} alt={market.title} className="w-full h-full object-cover" />
                      <img src={market.image2} alt={`${market.title} 2`} className="w-full h-full object-cover" />
                    </div>
                  )}
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    {market.title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed mb-8">
                    {market.description}
                  </p>
                  <Link
                    to="/contact"
                    className={`inline-flex items-center gap-2 bg-[#9B4B8C] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#7d3c71] transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                  >
                    {t.common.contactExperts}
                    <ArrowRight className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
                  </Link>
                </motion.div>
              </div>
            </div>
          </section>

          <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-10">{t.common.otherMarkets}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {relatedMarkets.map((m) => (
                  <Link key={m.id} to={`/markets/${m.slug}`}>
                    <div className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow group">
                      <div className="h-40 overflow-hidden">
                        <MarketImage
                          market={m}
                          imgClassName="group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 group-hover:text-[#9B4B8C] transition-colors">
                          {m.title}
                        </h3>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  // Markets listing page
  return (
    <div className="min-h-screen">
      <Header />
      <main dir={isRTL ? 'rtl' : 'ltr'}>
        <section className="pt-32 pb-20 bg-gradient-to-br from-[#e8d4e5] via-[#d9c5d6] to-[#c5a3c0]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t.marketsSection.title}
              </h1>
              <p className="text-lg text-gray-700 leading-relaxed">
                {t.marketsSection.description}
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {t.markets.map((market, index) => (
                <motion.div
                  key={market.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link to={`/markets/${market.slug}`} className="group block">
                    <div className="relative overflow-hidden rounded-xl bg-white shadow-lg hover:shadow-xl transition-shadow">
                      <div className="relative h-56 overflow-hidden">
                        <MarketImage
                          market={market}
                          imgClassName="transform group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-[#9B4B8C] text-white text-xs font-medium rounded-full">
                            {t.common.markets}
                          </span>
                        </div>
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-xl font-bold text-white">{market.title}</h3>
                        </div>
                      </div>
                      <div className="p-5">
                        <p className="text-gray-600 text-sm line-clamp-2">
                          {market.description}
                        </p>
                        <div className={`mt-4 flex items-center text-[#9B4B8C] text-sm font-medium ${isRTL ? 'flex-row-reverse' : ''}`}>
                          <span>{t.common.learnMore}</span>
                          <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'} transform group-hover:translate-x-1 transition-transform`} />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Markets;
